<script setup lang="ts">
import { defineProps, computed } from 'vue'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { formatMoney } from '@/lib/utils/money'
import { useTaxbackCredits } from '@/composables/useTaxbackCredits'

interface ProcessedForm {
  id: string | number
  year: number
  created_at: string | Date
  status: string
  snapshot?: Record<string, any>
  totalApprovedRefund: number
  categoryRefunds?: Record<string, number>
}

const props = defineProps<{
  forms: ProcessedForm[]
  defaultExpanded?: boolean
}>()

const { formatCategoryName } = useTaxbackCredits()

const totalRefund = computed(() =>
  props.forms.reduce((acc, f) => acc + (Number(f.totalApprovedRefund) || 0), 0),
)

const formsByYear = computed(() => {
  const grouped: Record<number, ProcessedForm[]> = {}
  props.forms.forEach((form) => {
    if (!grouped[form.year]) grouped[form.year] = []
    grouped[form.year].push(form)
  })
  return Object.entries(grouped).sort((a, b) => Number(b[0]) - Number(a[0]))
})
</script>

<template>
  <Accordion
    type="single"
    collapsible
    class="w-full"
    :defaultValue="defaultExpanded ? 'taxback-credits' : undefined"
  >
    <AccordionItem key="taxback-credits" value="taxback-credits">
      <AccordionTrigger class="text-lg font-medium">
        <div class="w-full flex justify-between items-center pr-4">
          <div>Taxback credits:</div>
          <div>{{ formatMoney(totalRefund) }}</div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <p v-if="forms.length === 0" class="text-center text-muted-foreground">
          No entries yet.
        </p>

        <div v-else class="relative">
          <!-- Timeline line -->
          <div
            class="absolute left-[19px] top-0 bottom-0 w-[2px] bg-muted"
          ></div>

          <div
            v-for="[year, yearForms] in formsByYear"
            :key="year"
            class="mb-8"
          >
            <!-- Year marker -->
            <div
              class="relative flex flex-col items-start bg-background z-10 py-4"
            >
              <div class="text-center">
                <div class="text-xl font-medium">{{ year }}</div>
                <div class="text-sm text-muted-foreground">Year</div>
              </div>
            </div>

            <!-- Forms for this year -->
            <div v-for="form in yearForms" :key="form.id" class="relative">
              <!-- Timeline dot -->
              <div
                class="absolute left-[15px] top-[30px] w-[10px] h-[10px] rounded-full bg-primary"
              ></div>

              <!-- Form details card -->
              <div class="ml-12 p-4 space-y-4">
                <div class="space-y-2">
                  <div class="flex justify-between items-center">
                    <span class="text-muted-foreground">Submitted at:</span>
                    <span>{{
                      Intl.DateTimeFormat('en-IE').format(
                        new Date(form.created_at as any),
                      )
                    }}</span>
                  </div>

                  <div class="flex justify-between items-center">
                    <span class="text-muted-foreground">Status:</span>
                    <span
                      :class="[
                        'font-bold rounded-full text-sm uppercase',
                        {
                          'text-yellow-500': form.status === 'pending',
                          'text-green-500': form.status === 'approved',
                          'text-red-500': form.status === 'rejected',
                        },
                      ]"
                    >
                      {{ form.status }}
                    </span>
                  </div>
                </div>

                <div
                  v-if="Object.keys(form.snapshot || {}).length > 0"
                  class="space-y-2 border-t pt-4"
                >
                  <h4 class="font-medium text-muted-foreground">
                    Approved Refunds by Category:
                  </h4>
                  <div
                    v-for="(_categoryData, categoryName) in form.snapshot"
                    :key="categoryName"
                    class="flex justify-between items-center"
                  >
                    <span>{{ formatCategoryName(categoryName) }}:</span>
                    <span class="font-medium">{{
                      formatMoney(form.categoryRefunds?.[categoryName] ?? 0)
                    }}</span>
                  </div>
                </div>

                <div class="flex justify-between items-center border-t pt-4">
                  <span class="font-medium">Total Approved Refund:</span>
                  <span class="font-medium text-lg">{{
                    formatMoney(form.totalApprovedRefund)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
</template>
